import React, { FC, ReactElement, useEffect, useRef, useState } from "react"
import { FlatList, Image, ImageStyle, SectionList, TextStyle, View, ViewStyle } from "react-native"

import { DrawerLayout, DrawerState } from "react-native-gesture-handler"
import { useSharedValue } from "react-native-reanimated"
import { SafeAreaView } from "react-native-safe-area-context"

import { ListItem, Screen, Text } from "../../components"
import { isRTL } from "../../i18n"
import { GuestTabScreenProps } from "../../navigators/GuestNavigator"
import { colors, spacing } from "../../theme"
import * as Demos from "./demos"
import { DrawerIconButton } from "./DrawerIconButton"

const logo = require("../../../assets/images/logo.png")

export interface Demo {
  name: string
  description: string
  data: ReactElement[]
}

export const DemoShowroomScreen: FC<GuestTabScreenProps<"Showroom">> = function DemoShowroomScreen(_props) {
  const [open, setOpen] = useState(false)
  const timeout = useRef<ReturnType<typeof setTimeout>>()
  const drawerRef = useRef<DrawerLayout>()
  const listRef = useRef<SectionList>()
  const menuRef = useRef<FlatList>()
  const progress = useSharedValue(0)

  const toggleDrawer = () => {
    if (!open) {
      setOpen(true)
      drawerRef.current?.openDrawer({ speed: 2 })
    } else {
      setOpen(false)
      drawerRef.current?.closeDrawer({ speed: 2 })
    }
  }

  const handleScroll = (sectionIndex: number, itemIndex = 0) => {
    listRef.current.scrollToLocation({
      animated: true,
      itemIndex,
      sectionIndex,
    })
    toggleDrawer()
  }

  const scrollToIndexFailed = (info: { index: number; highestMeasuredFrameIndex: number; averageItemLength: number }) => {
    listRef.current?.getScrollResponder()?.scrollToEnd()
    timeout.current = setTimeout(
      () =>
        listRef.current?.scrollToLocation({
          animated: true,
          itemIndex: info.index,
          sectionIndex: 0,
        }),
      50,
    )
  }

  useEffect(() => {
    return () => timeout.current && clearTimeout(timeout.current)
  }, [])

  return (
    <DrawerLayout
      ref={drawerRef}
      drawerBackgroundColor={colors.palette.neutral100}
      drawerPosition={isRTL ? "right" : "left"}
      drawerType="slide"
      drawerWidth={326}
      overlayColor={colors.palette.overlay20}
      renderNavigationView={() => (
        <SafeAreaView
          edges={["top"]}
          style={$drawer}
        >
          <View style={$logoContainer}>
            <Image
              source={logo}
              style={$logoImage}
            />
          </View>

          <FlatList<{ name: string; useCases: string[] }>
            ref={menuRef}
            contentContainerStyle={$flatListContentContainer}
            keyExtractor={(item) => item.name}
            data={Object.values(Demos).map((d) => ({
              name: d.name,
              useCases: d.data.map((u) => u.props.name),
            }))}
            renderItem={({ item, index: sectionIndex }) => (
              <View>
                <Text
                  preset="bold"
                  style={$menuContainer}
                  onPress={() => handleScroll(sectionIndex)}
                >
                  {item.name}
                </Text>
                {item.useCases.map((u, index) => (
                  <ListItem
                    key={`section${sectionIndex}-${u}`}
                    rightIcon={isRTL ? "caretLeft" : "caretRight"}
                    text={u}
                    onPress={() => handleScroll(sectionIndex, index + 1)}
                  />
                ))}
              </View>
            )}
          />
        </SafeAreaView>
      )}
      onDrawerSlide={(drawerProgress) => {
        progress.value = open ? 1 - drawerProgress : drawerProgress
      }}
      onDrawerStateChanged={(newState: DrawerState, drawerWillShow: boolean) => {
        if (newState === "Settling") {
          setOpen(drawerWillShow)
        }
      }}
    >
      <Screen
        contentContainerStyle={$screenContainer}
        preset="fixed"
        safeAreaEdges={["top", "bottom"]}
      >
        <DrawerIconButton
          onPress={toggleDrawer}
          {...{ open, progress }}
        />

        {/* <SectionList
          ref={listRef}
          contentContainerStyle={$sectionListContentContainer}
          renderItem={({ item }) => item}
          renderSectionFooter={() => <View style={$demoUseCasesSpacer} />}
          sections={Object.values(Demos)}
          stickySectionHeadersEnabled={false}
          ListHeaderComponent={
            <View style={$heading}>
              <Text
                preset="heading"
                tx="demoShowroomScreen.jumpStart"
              />
            </View>
          }
          renderSectionHeader={({ section }) => {
            return (
              <View>
                <Text
                  preset="heading"
                  style={$demoItemName}
                >
                  {section.name}
                </Text>
                <Text style={$demoItemDescription}>{section.description}</Text>
              </View>
            )
          }}
          onScrollToIndexFailed={scrollToIndexFailed}
        /> */}
      </Screen>
    </DrawerLayout>
  )
}

const $screenContainer: ViewStyle = {
  flex: 1,
}

const $drawer: ViewStyle = {
  flex: 1,
}

const $flatListContentContainer: ViewStyle = {
  paddingHorizontal: spacing.lg,
}

const $sectionListContentContainer: ViewStyle = {
  paddingHorizontal: spacing.lg,
}

const $heading: ViewStyle = {
  marginBottom: spacing.xxxl,
}

const $logoImage: ImageStyle = {
  height: 42,
  width: 77,
}

const $logoContainer: ViewStyle = {
  alignSelf: "flex-start",
  height: 56,
  paddingHorizontal: spacing.lg,
}

const $menuContainer: ViewStyle = {
  paddingBottom: spacing.xs,
  paddingTop: spacing.lg,
}

const $demoItemName: TextStyle = {
  fontSize: 24,
  marginBottom: spacing.md,
}

const $demoItemDescription: TextStyle = {
  marginBottom: spacing.xxl,
}

const $demoUseCasesSpacer: ViewStyle = {
  paddingBottom: spacing.xxl,
}

// @demo remove-file
